import { createFileRoute, Link } from "@tanstack/react-router";
import { queryOptions, useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { useEffect, useState, type ReactNode } from "react";
import { StorefrontLayout } from "@/components/storefront/Layout";
import { PickupSmsNotice } from "@/components/storefront/PickupSmsNotice";
import { getOrderByToken } from "@/lib/orders.functions";
import { formatPrice } from "@/lib/format";
import { optimizeImage, isDeadImageUrl } from "@/lib/image";
import { BANK_ACCOUNTS, BENEFICIARY } from "@/lib/bank-accounts";
import {
  CheckCircle2,
  Hash,
  CalendarDays,
  Mail,
  Phone,
  MapPin,
  CreditCard,
  Package,
  Landmark,
  Banknote,
  ShoppingBag,
  ReceiptText,
  Clock,
  ArrowRight,
  Inbox,
  PackageCheck,
  Truck,
  Home,
  Tag,
  FileText,
  AlertCircle,
  CalendarClock,
  Ruler,
  Info,
  ExternalLink,
} from "lucide-react";

const qo = (token: string) =>
  queryOptions({
    queryKey: ["order", token],
    queryFn: () => getOrderByToken({ data: { token } }),
  });

export const Route = createFileRoute("/order/$token")({
  head: ({ params }) => ({
    meta: [
      { title: "Ευχαριστούμε για την παραγγελία σου! — MrPanda.gr" },
      { property: "og:url", content: `https://mrpanda.gr/order/${params.token}` },
    ],
  }),
  validateSearch: (search: Record<string, unknown>): { view?: boolean } => ({
    view: search.view === true || search.view === "1" || search.view === 1,
  }),
  loader: ({ params, context }) => context.queryClient.ensureQueryData(qo(params.token)),
  component: OrderPage,
});

const PAYMENT_LABELS: Record<string, string> = {
  cash: "Μετρητά κατά την παράδοση",
  cod: "Αντικαταβολή",
  bank_transfer: "Τραπεζικό έμβασμα",
  iris: "IRIS άμεση πληρωμή",
  klarna: "Klarna",
  viva_card: "Κάρτα (Viva)",
  viva: "Online πληρωμή (Viva)",
};

const PAYMENT_STATUS_LABELS: Record<string, string> = {
  pending: "Σε εκκρεμότητα",
  awaiting_transfer: "Αναμονή εμβάσματος",
  awaiting_payment: "Αναμονή πληρωμής",
  paid: "Πληρώθηκε",
  failed: "Απέτυχε",
  refunded: "Επιστράφηκε",
};

/** Decorative confetti — client-only so randomized positions don't break SSR. */
function Confetti() {
  const [pieces, setPieces] = useState<
    Array<{ left: number; delay: number; duration: number; color: string; emoji?: string }>
  >([]);

  useEffect(() => {
    const colors = ["#f59e0b", "#fbbf24", "#34d399", "#60a5fa", "#f472b6", "#a78bfa"];
    const emojis = ["🐼", "🎉", "✨", "🎊", "💛"];
    const arr = Array.from({ length: 70 }, (_, i) => {
      const isEmoji = i % 7 === 0;
      return {
        left: Math.random() * 100,
        delay: Math.random() * 4,
        duration: 3 + Math.random() * 3,
        color: colors[Math.floor(Math.random() * colors.length)],
        emoji: isEmoji ? emojis[Math.floor(Math.random() * emojis.length)] : undefined,
      };
    });
    setPieces(arr);
    const t = setTimeout(() => setPieces([]), 9000);
    return () => clearTimeout(t);
  }, []);

  if (pieces.length === 0) return null;
  return (
    <div className="pointer-events-none fixed inset-0 z-50 overflow-hidden" aria-hidden>
      {pieces.map((p, i) => (
        <span
          key={i}
          className="mp-confetti-piece"
          style={{
            left: `${p.left}%`,
            animationDelay: `${p.delay}s`,
            animationDuration: `${p.duration}s`,
            background: p.emoji ? "transparent" : p.color,
            width: p.emoji ? "auto" : undefined,
            height: p.emoji ? "auto" : undefined,
            fontSize: p.emoji ? "1.1rem" : undefined,
          }}
        >
          {p.emoji}
        </span>
      ))}
    </div>
  );
}

/** Icon + friendly label for each known note line key. */
const NOTE_META: Array<{ test: RegExp; icon: ReactNode; label?: string }> = [
  { test: /^τύπος παραστατικού/i, icon: <FileText className="h-4 w-4" /> },
  { test: /^επωνυμία/i, icon: <FileText className="h-4 w-4" /> },
  { test: /^δου/i, icon: <FileText className="h-4 w-4" /> },
  { test: /^δραστηριότητα/i, icon: <FileText className="h-4 w-4" /> },
  { test: /ελλείψ/i, icon: <AlertCircle className="h-4 w-4" /> },
  { test: /παράθυρο παράδοσης|παραλαβή από κατάστημα/i, icon: <CalendarClock className="h-4 w-4" /> },
  { test: /απόσταση/i, icon: <Ruler className="h-4 w-4" /> },
  { test: /μεταφορική|κατάθεση|σχόλια courier/i, icon: <Truck className="h-4 w-4" /> },
];

function noteIcon(label: string): ReactNode {
  const m = NOTE_META.find((n) => n.test.test(label));
  return m?.icon ?? <Info className="h-4 w-4" />;
}

type ParsedNote = { label: string; value: string };

/** Split the composed multi-line notes string into labelled chips. */
function parseNotes(notes: string): ParsedNote[] {
  return notes
    .split("\n")
    .map((l) => l.trim())
    .filter(Boolean)
    .map((line) => {
      const idx = line.indexOf(":");
      if (idx === -1) return { label: "Σημείωση πελάτη", value: line };
      return { label: line.slice(0, idx).trim(), value: line.slice(idx + 1).trim() };
    })
    .filter((n) => n.value.length > 0);
}

type Step = { key: string; label: string; icon: ReactNode };
const TIMELINE_STEPS: Step[] = [
  { key: "received", label: "Λάβαμε την παραγγελία", icon: <Inbox className="h-5 w-5" /> },
  { key: "preparing", label: "Ετοιμάζουμε την παραγγελία", icon: <Package className="h-5 w-5" /> },
  { key: "packed", label: "Συσκευάσαμε την παραγγελία", icon: <PackageCheck className="h-5 w-5" /> },
  { key: "shipped", label: "Απεστάλη", icon: <Truck className="h-5 w-5" /> },
  { key: "delivered", label: "Παραδόθηκε", icon: <Home className="h-5 w-5" /> },
];

/** Visual progress tracker across the four fulfillment stages. */
function OrderTimeline({ current }: { current: number }) {
  return (
    <section className="mt-6 rounded-3xl border border-border bg-card p-6 shadow-sm">
      <ol className="flex flex-col gap-4 sm:flex-row sm:items-start sm:gap-0">
        {TIMELINE_STEPS.map((step, i) => {
          const done = i < current;
          const active = i === current;
          const reached = i <= current;
          return (
            <li key={step.key} className="flex flex-1 items-center gap-3 sm:flex-col sm:text-center">
              <div className="flex items-center sm:w-full">
                {/* connector left (desktop) */}
                <span
                  className={`hidden h-1 flex-1 rounded-full sm:block ${i === 0 ? "opacity-0" : done || active ? "bg-primary" : "bg-border"}`}
                />
                <span
                  className={`relative grid h-11 w-11 shrink-0 place-items-center rounded-full border-2 transition-colors ${
                    reached
                      ? "border-primary bg-gradient-to-br from-primary to-amber-500 text-primary-foreground shadow-md shadow-primary/30"
                      : "border-border bg-muted text-muted-foreground"
                  } ${active ? "animate-success-pop ring-4 ring-primary/20" : ""}`}
                >
                  {done ? <CheckCircle2 className="h-5 w-5" /> : step.icon}
                </span>
                <span
                  className={`hidden h-1 flex-1 rounded-full sm:block ${i === TIMELINE_STEPS.length - 1 ? "opacity-0" : done ? "bg-primary" : "bg-border"}`}
                />
              </div>
              <span
                className={`text-sm font-semibold sm:mt-2 ${reached ? "text-foreground" : "text-muted-foreground"}`}
              >
                {step.label}
              </span>
            </li>
          );
        })}
      </ol>
    </section>
  );
}

function OrderPage() {
  const { token } = Route.useParams();
  const { view: viewMode } = Route.useSearch();
  const { data } = useSuspenseQuery(qo(token));
  const order = data.order as Record<string, unknown> | null;
  const items = data.items as Array<Record<string, unknown>>;
  const queryClient = useQueryClient();

  // A Viva payment can be confirmed a few seconds after the shopper lands here:
  // the confirmation webhook may lag, or never arrive at all. While the order
  // still shows as unpaid, keep re-checking so the page flips to the real order
  // on its own instead of leaving the shopper on a "pending" screen.
  const paymentProbeKey = order
    ? `${String((order.raw as { payment_provider?: unknown } | null)?.payment_provider ?? "")}|${String(order.payment_method ?? "")}|${String(order.payment_status ?? "")}`
    : "";
  useEffect(() => {
    if (!paymentProbeKey) return;
    const [provider, pm, status] = paymentProbeKey.split("|");
    const method = provider === "klarna" ? "klarna" : pm;
    const isOnline =
      method === "viva" || method === "viva_card" || method === "klarna" || method === "iris";
    if (!isOnline || status === "paid" || status === "refunded" || status === "failed") return;
    let tries = 0;
    const timer = window.setInterval(() => {
      tries += 1;
      // ~3 minutes of checking; the periodic server sweep covers anything later.
      if (tries > 22) {
        window.clearInterval(timer);
        return;
      }
      void queryClient.invalidateQueries({ queryKey: ["order", token] });
    }, 8000);
    return () => window.clearInterval(timer);
  }, [paymentProbeKey, token, queryClient]);


  // Fire the GA4 / Google Ads "purchase" conversion exactly once per order.
  // This is what Google Merchant Center / GA4 read to count conversions.
  useEffect(() => {
    if (typeof window === "undefined" || !order) return;
    // In "view" mode (opened from the account order history) this is not a
    // fresh checkout — never re-fire the purchase conversion.
    if (viewMode) return;
    const gtag = window.gtag;
    if (typeof gtag !== "function") return; // analytics not loaded (no consent)

    const pm = String(order.payment_method ?? "");
    const paymentStatus = String(order.payment_status ?? "");
    const isOnline = pm === "viva" || pm === "viva_card" || pm === "klarna" || pm === "iris";
    // Online orders only count once actually paid; offline (cash/bank) count on placement.
    if (isOnline && paymentStatus !== "paid") return;

    const transactionId = String(order.external_id ?? order.order_number ?? token);
    const dedupeKey = `mp_purchase_${transactionId}`;
    try {
      if (localStorage.getItem(dedupeKey)) return;
      localStorage.setItem(dedupeKey, "1");
    } catch {
      /* private mode — fall through and still fire */
    }

    gtag("event", "purchase", {
      transaction_id: transactionId,
      value: Number(order.total) || 0,
      shipping: Number(order.shipping_cost) || 0,
      currency: "EUR",
      items: items.map((it) => ({
        item_id: String(it.sku ?? it.barcode ?? it.id ?? ""),
        item_name: String(it.name ?? ""),
        quantity: Number(it.quantity) || 1,
      })),
    });
  }, [order, items, token, viewMode]);

  if (!order) {
    return (
      <StorefrontLayout>
        <div className="mx-auto max-w-2xl px-4 py-16 text-center">
          <div className="mx-auto grid h-24 w-24 place-items-center rounded-full bg-accent text-6xl animate-bob">🐼</div>
          <h1 className="mt-6 text-xl font-bold">Δεν βρέθηκε παραγγελία</h1>
          <p className="mt-2 text-sm text-muted-foreground">Ο σύνδεσμος ίσως έχει λήξει ή δεν είναι σωστός.</p>
          <Link to="/" className="mp-bouncy mt-6 inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 font-semibold text-primary-foreground hover:opacity-90">
            Επιστροφή στην αρχική
          </Link>
        </div>
      </StorefrontLayout>
    );
  }

  // Direct Klarna orders are stored in the shared orders table as "viva"
  // (its payment_method check constraint); the precise provider lives in
  // raw.payment_provider — surface it so the customer sees "Klarna".
  const rawProvider = (order.raw as { payment_provider?: unknown } | null)?.payment_provider;
  const pm = rawProvider === "klarna" ? "klarna" : String(order.payment_method);
  const isBank = pm === "bank_transfer";
  const isCash = pm === "cash";
  const paymentStatus = String(order.payment_status);
  const paymentStatusLabel = PAYMENT_STATUS_LABELS[paymentStatus] ?? paymentStatus;
  const paymentLabel = PAYMENT_LABELS[pm] ?? pm;
  const isOnline = pm === "viva" || pm === "viva_card" || pm === "klarna" || pm === "iris";
  // Online (Viva) orders are only "real" once payment is confirmed. Until then
  // we must not present the order as completed.
  const awaitingPayment = isOnline && paymentStatus !== "paid" && paymentStatus !== "refunded";
  const vivaCheckoutUrl = order.viva_checkout_url ? String(order.viva_checkout_url) : null;
  const orderNumber = order.order_number ? String(order.order_number) : null;
  const externalId = String(order.external_id);
  const customerName = order.customer_name ? String(order.customer_name) : "";
  const firstName = customerName.trim().split(/\s+/)[0] ?? "";

  const createdAt = order.created_at ? new Date(String(order.created_at)) : null;
  const dateText = createdAt
    ? new Intl.DateTimeFormat("el-GR", {
        day: "2-digit",
        month: "long",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      }).format(createdAt)
    : "—";

  const addr = (order.shipping_address as Record<string, unknown> | null) ?? null;
  const addrLine = addr
    ? [
        addr.line1 ?? addr.address1,
        addr.line2 ?? addr.address2,
        addr.postalCode ?? addr.postcode,
        addr.city,
        addr.region,
        addr.country,
      ]
        .map((v) => (typeof v === "string" ? v.trim() : ""))
        .filter(Boolean)
        .join(", ")
    : "";

  const notes = addr && typeof addr.notes === "string" ? addr.notes.trim() : "";
  const parsedNotes = notes ? parseNotes(notes) : [];
  // Store-pickup orders get a playful "wait for the SMS" reminder.
  const isStorePickup =
    (typeof addr?.shipping_method === "string" && addr.shipping_method === "store_pickup") ||
    /Παραλαβή από/i.test(notes);

  // Fulfillment state for the progress timeline.
  const status = String(order.status ?? "");
  const source = order.source ? String(order.source) : null;
  const isEshop = source === "eshop" || source == null;
  const trackingNumber = order.tracking_number ? String(order.tracking_number) : null;
  const voucherUrl = order.voucher_url ? String(order.voucher_url) : null;
  const carrierName = order.carrier_name ? String(order.carrier_name) : null;
  const deliveredAt = order.delivered_at ? String(order.delivered_at) : null;
  const isDelivered = isEshop
    ? status === "delivered" || status === "completed" || Boolean(deliveredAt)
    : status !== "cancelled";
  const isShipped = isDelivered || status === "shipped";
  // The parcel is considered "packed" once a courier voucher/tracking exists,
  // or once it has already shipped/been delivered.
  const isPacked = isShipped || Boolean(voucherUrl) || Boolean(trackingNumber);
  // The order is "in preparation" once the OPS system starts working on it
  // (status processing / preparing / in_progress), even before a voucher exists.
  const normalizedStatus = status.toLowerCase().replace(/[\s_-]+/g, " ").trim();
  const isPreparing =
    isPacked ||
    normalizedStatus === "processing" ||
    normalizedStatus === "preparing" ||
    normalizedStatus === "in progress" ||
    normalizedStatus === "in preparation";
  // 0 = received, 1 = preparing, 2 = packed, 3 = shipped, 4 = delivered.
  const timelineStep = isDelivered ? 4 : isShipped ? 3 : isPacked ? 2 : isPreparing ? 1 : 0;

  // Online (Viva) orders that have not been paid yet must NOT be shown as a
  // real order. Show only a minimal payment-pending notice (no order details).
  if (awaitingPayment) {
    return (
      <StorefrontLayout hideCartSidebar>
        <div className="mx-auto max-w-md px-4 py-16 text-center">
          <div className="relative mx-auto grid h-24 w-24 place-items-center">
            <span className="grid h-24 w-24 place-items-center rounded-full bg-gradient-to-br from-amber-400 to-amber-500 text-5xl shadow-lg shadow-amber-500/30">
              ⏳
            </span>
            <Clock className="absolute -bottom-1 -right-1 h-9 w-9 rounded-full bg-background text-amber-500" />
          </div>
          <h1 className="mt-6 text-2xl font-bold">
            {paymentStatus === "failed" ? "Η πληρωμή δεν ολοκληρώθηκε" : "Εκκρεμεί η πληρωμή"}
          </h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Η παραγγελία σου θα καταχωρηθεί μόλις ολοκληρωθεί η πληρωμή. Αν δεν ολοκληρώσεις
            την πληρωμή, δεν δημιουργείται παραγγελία.
          </p>
          {vivaCheckoutUrl && (
            <a
              href={vivaCheckoutUrl}
              className="mp-bouncy mt-6 inline-flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-amber-500 to-amber-600 px-7 py-3 font-bold text-white shadow-md shadow-amber-500/30 hover:opacity-95"
            >
              <CreditCard className="h-5 w-5" /> Ολοκλήρωσε την πληρωμή
              <ArrowRight className="h-5 w-5" />
            </a>
          )}
          <div className="mt-4">
            <Link to="/" className="text-sm font-medium text-primary underline">
              Επιστροφή στην αρχική
            </Link>
          </div>
        </div>
      </StorefrontLayout>
    );
  }

  // ── "View" mode ──────────────────────────────────────────────────────────
  // Opened from the account order history: a calm, informational order sheet
  // (with per-product prices) — deliberately different from the celebratory
  // post-checkout confirmation below.
  if (viewMode) {
    const stepLabel = TIMELINE_STEPS[timelineStep]?.label ?? "Σε επεξεργασία";
    return (
      <StorefrontLayout hideCartSidebar>
        <div className="px-4 py-8 lg:px-6">
          {/* Header */}
          <div className="mb-3">
            <Link
              to="/account/orders"
              className="inline-flex items-center gap-1.5 text-sm font-medium text-muted-foreground transition-colors hover:text-primary"
            >
              <ArrowRight className="h-4 w-4 rotate-180" /> Οι παραγγελίες μου
            </Link>
          </div>
          <header className="rounded-3xl border border-border bg-card p-6 shadow-sm">
            <div className="flex flex-wrap items-center gap-4">
              <span className="grid h-14 w-14 shrink-0 place-items-center rounded-2xl bg-gradient-to-br from-primary to-amber-500 text-3xl shadow-md shadow-primary/25">
                🐼
              </span>
              <div className="min-w-0 flex-1">
                <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  Λεπτομέρειες παραγγελίας
                </p>
                <h1 className="flex items-center gap-2 text-2xl font-extrabold tracking-tight">
                  <Hash className="h-5 w-5 text-primary" />
                  <span className="font-mono">{orderNumber ?? externalId}</span>
                </h1>
                <p className="mt-1 text-sm text-muted-foreground">{dateText}</p>
              </div>
              <div className="flex flex-col items-end gap-1.5">
                <span className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 px-3 py-1 text-sm font-semibold text-primary">
                  <Truck className="h-4 w-4" /> {stepLabel}
                </span>
                <span className="text-price text-xl font-bold">{formatPrice(Number(order.total))}</span>
              </div>
            </div>
          </header>

          {/* Fulfillment progress */}
          <OrderTimeline current={timelineStep} />

          {/* Tracking / voucher */}
          {(trackingNumber || voucherUrl) && (
            <section className="mt-4 rounded-2xl border border-primary/30 bg-primary/5 p-5">
              <h2 className="mb-3 flex items-center gap-2 font-bold">
                <span className="grid h-9 w-9 place-items-center rounded-xl bg-gradient-to-br from-primary to-amber-500 text-primary-foreground">
                  <Truck className="h-5 w-5" />
                </span>
                Παρακολούθηση αποστολής
              </h2>
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="min-w-0">
                  {carrierName && <p className="text-xs text-muted-foreground">Μεταφορική: {carrierName}</p>}
                  {trackingNumber && (
                    <p className="mt-0.5 flex items-center gap-2 text-sm">
                      <span className="text-muted-foreground">Αριθμός αποστολής (tracking):</span>
                      <span className="select-all font-mono font-bold tracking-wide text-foreground">{trackingNumber}</span>
                    </p>
                  )}
                </div>
                {voucherUrl && (
                  <a
                    href={voucherUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mp-bouncy inline-flex shrink-0 items-center justify-center gap-2 rounded-full border-2 border-primary/30 bg-card px-5 py-2.5 text-sm font-semibold text-primary hover:bg-primary/10"
                  >
                    <Tag className="h-4 w-4" /> Voucher αποστολής
                    <ExternalLink className="h-4 w-4" />
                  </a>
                )}
              </div>
            </section>
          )}

          {/* Products with per-item prices */}
          <section className="mt-4 rounded-2xl border border-border bg-card p-5">
            <h2 className="mb-3 flex items-center gap-2 font-bold">
              <span className="grid h-9 w-9 place-items-center rounded-xl bg-gradient-to-br from-primary to-amber-500 text-primary-foreground">
                <Package className="h-5 w-5" />
              </span>
              Προϊόντα
              <span className="ml-auto rounded-full bg-muted px-2.5 py-0.5 text-xs font-semibold text-muted-foreground">
                {items.reduce((s, it) => s + Number(it.quantity), 0)} τεμ.
              </span>
            </h2>
            <ul className="divide-y divide-border">
              {items.map((it) => {
                const qty = Number(it.quantity) || 1;
                const unit = it.unit_price != null ? Number(it.unit_price) : null;
                const line = unit != null ? unit * qty : null;
                return (
                  <li key={String(it.id)} className="flex items-center gap-3 py-3">
                    <div className="relative grid h-16 w-16 shrink-0 place-items-center overflow-hidden rounded-xl border border-border bg-white">
                      {it.image_url && !isDeadImageUrl(String(it.image_url)) ? (
                        <img
                          src={optimizeImage(String(it.image_url), { width: 96 })}
                          alt={String(it.name)}
                          className="h-full w-full object-contain"
                          loading="lazy"
                          decoding="async"
                        />
                      ) : (
                        <span className="text-2xl">🐼</span>
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold">{String(it.name)}</p>
                      <p className="mt-0.5 text-xs text-muted-foreground">
                        {unit != null ? (
                          <>
                            {qty} × {formatPrice(unit)}
                          </>
                        ) : (
                          <>Ποσότητα: {qty}</>
                        )}
                      </p>
                    </div>
                    <div className="shrink-0 text-right">
                      {line != null ? (
                        <span className="text-price font-bold">{formatPrice(line)}</span>
                      ) : (
                        <span className="rounded-full bg-muted px-2 py-0.5 text-xs font-semibold text-muted-foreground">
                          ×{qty}
                        </span>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
            <dl className="mt-3 space-y-1 border-t border-border pt-3 text-sm">
              <div className="flex justify-between">
                <dt className="text-muted-foreground">Υποσύνολο</dt>
                <dd>{formatPrice(Number(order.subtotal))}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-muted-foreground">Μεταφορικά</dt>
                <dd>{Number(order.shipping_cost) === 0 ? "Δωρεάν" : formatPrice(Number(order.shipping_cost))}</dd>
              </div>
              <div className="flex justify-between border-t border-border pt-2 text-base font-bold">
                <dt>Σύνολο</dt>
                <dd className="text-price">{formatPrice(Number(order.total))}</dd>
              </div>
            </dl>
          </section>

          {/* Info cards */}
          <section className="mt-4 grid gap-3 sm:grid-cols-2">
            <InfoCard icon={<CreditCard className="h-5 w-5" />} label="Τρόπος πληρωμής" value={`${paymentLabel} · ${paymentStatusLabel}`} />
            <InfoCard icon={<Mail className="h-5 w-5" />} label="Email" value={String(order.customer_email)} />
            {Boolean(order.customer_phone) && (
              <InfoCard icon={<Phone className="h-5 w-5" />} label="Τηλέφωνο" value={String(order.customer_phone)} />
            )}
          </section>

          {/* Shipping address */}
          {addrLine && (
            <section className="mt-4 rounded-2xl border border-border bg-card p-5">
              <h2 className="mb-2 flex items-center gap-2 font-bold">
                <span className="grid h-9 w-9 place-items-center rounded-xl bg-accent text-accent-foreground">
                  <MapPin className="h-5 w-5" />
                </span>
                Στοιχεία αποστολής
              </h2>
              {customerName && <p className="text-sm font-semibold">{customerName}</p>}
              <p className="text-sm text-muted-foreground">{addrLine}</p>
              {isStorePickup && <PickupSmsNotice className="mt-3" />}
              {parsedNotes.length > 0 && (
                <dl className="mt-4 grid gap-2.5 sm:grid-cols-2">
                  {parsedNotes.map((n, i) => (
                    <div key={i} className="flex items-start gap-3 rounded-xl border border-border bg-muted/40 p-3">
                      <span className="mt-0.5 grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-accent text-accent-foreground">
                        {noteIcon(n.label)}
                      </span>
                      <div className="min-w-0">
                        <dt className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{n.label}</dt>
                        <dd className="whitespace-pre-line text-sm font-medium text-foreground">{n.value}</dd>
                      </div>
                    </div>
                  ))}
                </dl>
              )}
            </section>
          )}

          {/* Bank transfer details */}
          {isBank && (
            <section className="mt-4 rounded-2xl border border-border bg-card p-5">
              <h2 className="mb-2 flex items-center gap-2 font-bold">
                <span className="grid h-9 w-9 place-items-center rounded-xl bg-accent text-accent-foreground">
                  <Landmark className="h-5 w-5" />
                </span>
                Στοιχεία για τραπεζικό έμβασμα
              </h2>
              <p className="mb-3 text-sm text-muted-foreground">
                Ποσό: <span className="font-bold text-foreground">{formatPrice(Number(order.total))}</span> · Δικαιούχος:{" "}
                <span className="font-bold text-foreground">{BENEFICIARY}</span> · Αιτιολογία:{" "}
                <span className="font-mono text-foreground">{orderNumber ?? externalId}</span>
              </p>
              <ul className="divide-y divide-border rounded-xl border border-border">
                {BANK_ACCOUNTS.map((b) => (
                  <li key={b.iban} className="flex flex-col gap-1 p-3 sm:flex-row sm:items-center sm:justify-between">
                    <span className="text-sm font-semibold">{b.bank}</span>
                    <span className="break-all font-mono text-xs sm:text-sm">{b.iban}</span>
                  </li>
                ))}
              </ul>
            </section>
          )}

          <p className="mt-5 text-center text-xs text-muted-foreground">
            Οποιαδήποτε ενημέρωση για την παραγγελία σου θα σου έρθει και στο email{" "}
            <span className="font-semibold text-foreground">{String(order.customer_email)}</span>.
          </p>
        </div>
      </StorefrontLayout>
    );
  }

  return (
    <StorefrontLayout hideCartSidebar>
      <Confetti />
      <div className="mx-auto max-w-none px-4 py-10">
        {/* Hero */}
        <header className="mp-playful-panel relative overflow-hidden rounded-3xl border border-border p-8 text-center shadow-sm">
          <div className="pointer-events-none absolute -left-6 -top-6 text-5xl opacity-20 animate-float-y">🎈</div>
          <div className="pointer-events-none absolute -right-4 top-8 text-4xl opacity-20 animate-float-y" style={{ animationDelay: "1.2s" }}>🎁</div>

          <div className="relative mx-auto grid h-24 w-24 place-items-center animate-success-pop">
            <span className="mp-ring-pulse absolute inset-0 rounded-full" />
            <span className="grid h-24 w-24 place-items-center rounded-full bg-gradient-to-br from-primary to-amber-500 text-5xl shadow-lg shadow-primary/30 animate-bob">🐼</span>
            <CheckCircle2 className="absolute -bottom-1 -right-1 h-9 w-9 rounded-full bg-background text-success" />
          </div>

          <h1 className="mt-6 text-3xl font-extrabold tracking-tight sm:text-4xl">
            Ευχαριστούμε{firstName ? `, ${firstName}` : ""}! 🎉
          </h1>
          <p className="mx-auto mt-3 max-w-lg text-muted-foreground">
            Η παραγγελία σου καταχωρήθηκε με επιτυχία και ο πάντα μας την ετοιμάζει με αγάπη. 💛
          </p>

          {orderNumber && (
            <div className="mt-5 inline-flex max-w-full flex-wrap items-center justify-center gap-x-2 gap-y-1 rounded-2xl border-2 border-primary/30 bg-primary/10 px-4 py-2 text-primary animate-pop-in sm:rounded-full sm:px-5">
              <Hash className="h-4 w-4 shrink-0" />
              <span className="text-sm font-semibold">Αριθμός παραγγελίας</span>
              <span className="break-all font-mono text-sm font-bold tracking-wide sm:text-base">{orderNumber}</span>
            </div>
          )}
        </header>

        {/* Fulfillment progress */}
        <OrderTimeline current={timelineStep} />

        {/* Tracking / voucher */}
        {(trackingNumber || voucherUrl) && (
          <section className="mt-4 rounded-2xl border border-primary/30 bg-primary/5 p-5">
            <h2 className="mb-3 flex items-center gap-2 font-bold">
              <span className="grid h-9 w-9 place-items-center rounded-xl bg-gradient-to-br from-primary to-amber-500 text-primary-foreground">
                <Truck className="h-5 w-5" />
              </span>
              Παρακολούθηση αποστολής
            </h2>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="min-w-0">
                {carrierName && (
                  <p className="text-xs text-muted-foreground">Μεταφορική: {carrierName}</p>
                )}
                {trackingNumber && (
                  <p className="mt-0.5 flex items-center gap-2 text-sm">
                    <span className="text-muted-foreground">Αριθμός αποστολής (tracking):</span>
                    <span className="break-all select-all font-mono font-bold tracking-wide text-foreground">
                      {trackingNumber}
                    </span>
                  </p>
                )}
              </div>
              {voucherUrl && (
                <a
                  href={voucherUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mp-bouncy inline-flex shrink-0 items-center justify-center gap-2 rounded-full border-2 border-primary/30 bg-card px-5 py-2.5 text-sm font-semibold text-primary hover:bg-primary/10"
                >
                  <Tag className="h-4 w-4" /> Voucher αποστολής
                  <ExternalLink className="h-4 w-4" />
                </a>
              )}
            </div>
          </section>
        )}

        {/* Quick info cards */}
        <section className="mt-6 grid gap-3 sm:grid-cols-2">
          <InfoCard icon={<CalendarDays className="h-5 w-5" />} label="Ημερομηνία" value={dateText} />
          <InfoCard icon={<CreditCard className="h-5 w-5" />} label="Τρόπος πληρωμής" value={`${paymentLabel} · ${paymentStatusLabel}`} />
          <InfoCard icon={<Mail className="h-5 w-5" />} label="Email" value={String(order.customer_email)} />
          {Boolean(order.customer_phone) && (
            <InfoCard icon={<Phone className="h-5 w-5" />} label="Τηλέφωνο" value={String(order.customer_phone)} />
          )}
        </section>

        {/* Shipping address */}
        {addrLine && (
          <section className="mt-4 rounded-2xl border border-border bg-card p-5">
            <h2 className="mb-2 flex items-center gap-2 font-bold">
              <span className="grid h-9 w-9 place-items-center rounded-xl bg-accent text-accent-foreground"><MapPin className="h-5 w-5" /></span>
              Στοιχεία αποστολής
            </h2>
            {customerName && <p className="text-sm font-semibold">{customerName}</p>}
            <p className="text-sm text-muted-foreground">{addrLine}</p>
            {isStorePickup && <PickupSmsNotice className="mt-3" />}
            {parsedNotes.length > 0 && (
              <dl className="mt-4 grid gap-2.5 sm:grid-cols-2">
                {parsedNotes.map((n, i) => (
                  <div
                    key={i}
                    className="flex items-start gap-3 rounded-xl border border-border bg-muted/40 p-3"
                  >
                    <span className="mt-0.5 grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-accent text-accent-foreground">
                      {noteIcon(n.label)}
                    </span>
                    <div className="min-w-0">
                      <dt className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                        {n.label}
                      </dt>
                      <dd className="whitespace-pre-line text-sm font-medium text-foreground">
                        {n.value}
                      </dd>
                    </div>
                  </div>
                ))}
              </dl>
            )}
          </section>
        )}

        {/* Bank transfer */}
        {isBank && (
          <section className="mt-4 rounded-2xl border border-border bg-card p-5">
            <h2 className="mb-2 flex items-center gap-2 font-bold">
              <span className="grid h-9 w-9 place-items-center rounded-xl bg-accent text-accent-foreground"><Landmark className="h-5 w-5" /></span>
              Στοιχεία για τραπεζικό έμβασμα
            </h2>
            <p className="mb-3 text-sm text-muted-foreground">
              Ποσό: <span className="font-bold text-foreground">{formatPrice(Number(order.total))}</span> ·
              Δικαιούχος: <span className="font-bold text-foreground">{BENEFICIARY}</span> ·
              Αιτιολογία: <span className="font-mono text-foreground">{orderNumber ?? externalId}</span>
            </p>
            <ul className="divide-y divide-border rounded-xl border border-border">
              {BANK_ACCOUNTS.map((b) => (
                <li key={b.iban} className="flex flex-col gap-1 p-3 sm:flex-row sm:items-center sm:justify-between">
                  <span className="text-sm font-semibold">{b.bank}</span>
                  <span className="font-mono text-xs sm:text-sm">{b.iban}</span>
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* Cash */}
        {isCash && (
          <section className="mt-4 rounded-2xl border border-border bg-card p-5">
            <h2 className="mb-1 flex items-center gap-2 font-bold">
              <span className="grid h-9 w-9 place-items-center rounded-xl bg-accent text-accent-foreground"><Banknote className="h-5 w-5" /></span>
              Πληρωμή με μετρητά
            </h2>
            <p className="text-sm text-muted-foreground">Θα πληρώσεις {formatPrice(Number(order.total))} σε μετρητά κατά την παράδοση ή την παραλαβή από το κατάστημα.</p>
          </section>
        )}

        {/* Products */}
        <section className="mt-4 rounded-2xl border border-border bg-card p-5">
          <h2 className="mb-3 flex items-center gap-2 font-bold">
            <span className="grid h-9 w-9 place-items-center rounded-xl bg-gradient-to-br from-primary to-amber-500 text-primary-foreground"><Package className="h-5 w-5" /></span>
            Τα προϊόντα σου
            <span className="ml-auto rounded-full bg-muted px-2.5 py-0.5 text-xs font-semibold text-muted-foreground">
              {items.reduce((s, it) => s + Number(it.quantity), 0)} τεμ.
            </span>
          </h2>
          <ul className="divide-y divide-border">
            {items.map((it) => (
              <li key={String(it.id)}>
                <Link
                  to="/search"
                  search={{ q: String(it.name) }}
                  className="group -mx-2 flex items-center gap-3 rounded-xl px-2 py-3 transition-colors hover:bg-muted/60"
                >
                  <div className="relative grid h-14 w-14 shrink-0 place-items-center overflow-hidden rounded-xl border border-border bg-white">
                    {it.image_url && !isDeadImageUrl(String(it.image_url)) ? (
                      <img src={optimizeImage(String(it.image_url), { width: 80 })} alt={String(it.name)} width={80} height={80} className="h-full w-full object-contain transition-transform group-hover:scale-105" loading="lazy" decoding="async" />
                    ) : (
                      <span className="text-2xl">🐼</span>
                    )}
                    <span className="absolute -right-1 -top-1 grid h-5 min-w-5 place-items-center rounded-full bg-primary px-1 text-[10px] font-bold text-primary-foreground">{String(it.quantity)}</span>
                  </div>
                  <span className="flex-1 text-sm font-medium group-hover:text-primary group-hover:underline">{String(it.name)}</span>
                  <ArrowRight className="h-4 w-4 shrink-0 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100" />
                </Link>
              </li>
            ))}
          </ul>
          <dl className="mt-3 space-y-1 border-t border-border pt-3 text-sm">
            <div className="flex justify-between"><dt className="text-muted-foreground">Υποσύνολο</dt><dd>{formatPrice(Number(order.subtotal))}</dd></div>
            <div className="flex justify-between"><dt className="text-muted-foreground">Μεταφορικά</dt><dd>{Number(order.shipping_cost) === 0 ? <span className="font-semibold text-success">Δωρεάν 🎉</span> : formatPrice(Number(order.shipping_cost))}</dd></div>
            <div className="flex justify-between text-base font-bold"><dt>Σύνολο</dt><dd className="text-price">{formatPrice(Number(order.total))}</dd></div>
          </dl>
        </section>

        {/* CTAs */}
        <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:justify-center">
          <Link to="/" className="mp-bouncy inline-flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-primary to-amber-500 px-6 py-3 font-bold text-primary-foreground shadow-md shadow-primary/20 hover:opacity-95">
            <ShoppingBag className="h-5 w-5" /> Συνέχισε τα ψώνια
          </Link>
          <Link to="/account/orders" className="mp-bouncy inline-flex items-center justify-center gap-2 rounded-full border-2 border-primary/30 bg-primary/5 px-6 py-3 font-semibold text-primary hover:bg-primary/10">
            <ReceiptText className="h-5 w-5" /> Οι παραγγελίες μου
          </Link>
        </div>

        <p className="mt-5 text-center text-xs text-muted-foreground">
          Αποθήκευσε αυτή τη σελίδα για παρακολούθηση. Σου στείλαμε email επιβεβαίωσης στη
          διεύθυνση{" "}
          <span className="font-semibold text-foreground">{String(order.customer_email)}</span>.
        </p>
      </div>
    </StorefrontLayout>
  );
}

function InfoCard({ icon, label, value }: { icon: ReactNode; label: string; value: string }) {
  return (
    <div className="flex items-center gap-3 rounded-2xl border border-border bg-card p-4">
      <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-accent text-accent-foreground">{icon}</span>
      <div className="min-w-0">
        <p className="text-xs text-muted-foreground">{label}</p>
        <p className="break-all text-sm font-semibold">{value}</p>
      </div>
    </div>
  );
}